import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createSecureEventSource } from '../sse/helper';
import { useMe } from '../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';

const POLL_INTERVAL_MS = 5000;

interface UseAlertsLiveUpdatesOptions {
  fetchUpdatedAlert: (alertId: number) => void;
  refreshAlerts: () => Promise<void> | void;
}

export const useAlertsLiveUpdates = ({ fetchUpdatedAlert, refreshAlerts }: UseAlertsLiveUpdatesOptions): void => {
  const { features, isLoading } = useMe();
  const pollingEnabled = !isLoading && isFeatureEnabled(ORGANISATION_FEATURE.ALERT_UPDATES_POLLING, features);

  // Keep the latest refresh callback in a ref so the query fn stays referentially stable.
  const refreshAlertsRef = useRef(refreshAlerts);
  useEffect(() => {
    refreshAlertsRef.current = refreshAlerts;
  }, [refreshAlerts]);

  useQuery({
    queryKey: ['alertsBacklogPoll'],
    queryFn: async () => {
      await refreshAlertsRef.current();
      return null;
    },
    enabled: pollingEnabled,
    refetchInterval: POLL_INTERVAL_MS,
    gcTime: 0,
  });

  useEffect(() => {
    if (isLoading || pollingEnabled) {
      return;
    }

    const eventSource = createSecureEventSource('/v1/sse/alerts', (event: MessageEvent) => {
      fetchUpdatedAlert(Number(event.data));
    });

    return () => {
      eventSource.then((it) => it.close());
    };
  }, [isLoading, pollingEnabled, fetchUpdatedAlert]);
};
